import { apiClient } from './client';

/**
 * Client for `/api/teams/*` (ADR-047 — cross-cutting teams).
 *
 * A department is a person's single functional home; a team is who they work
 * with, and a person can be on several. The Leadership Team is the motivating
 * case: the integrator reports to the visionary on the accountability chart
 * AND sits on leadership, while a department head runs their function AND
 * sits on leadership. Neither fits a single `departmentId` scalar.
 *
 * Reads are open to any org member; mutations need visionary/integrator.
 */

export type TeamKind = 'leadership' | 'custom';

export interface TeamMemberView {
  employeeId: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string | null;
  /** The member keeps their own department while on the team. */
  departmentId: string | null;
  departmentName: string | null;
}

export interface TeamView {
  id: string;
  name: string;
  slug: string;
  kind: TeamKind;
  description: string | null;
  order: number;
  archivedAt: string | null;
  memberCount: number;
  members: TeamMemberView[];
}

/** The compact shape embedded on an employee row by `GET /api/employees`. */
export interface EmployeeTeamRef {
  id: string;
  name: string;
  slug: string;
  kind: TeamKind;
}

export async function listTeams(includeArchived = false): Promise<TeamView[]> {
  const qs = includeArchived ? '?includeArchived=true' : '';
  const res = await apiClient.get<{ data: TeamView[] }>(`/api/teams${qs}`);
  return res.data.data;
}

export async function getTeam(id: string): Promise<TeamView> {
  const res = await apiClient.get<{ data: TeamView }>(`/api/teams/${encodeURIComponent(id)}`);
  return res.data.data;
}

export interface CreateTeamBody {
  name: string;
  slug?: string;
  description?: string | null;
  order?: number;
  employeeIds?: string[];
}

export async function createTeam(body: CreateTeamBody): Promise<TeamView> {
  const res = await apiClient.post<{ data: TeamView }>('/api/teams', body);
  return res.data.data;
}

export async function updateTeam(
  id: string,
  body: { name?: string; description?: string | null; order?: number },
): Promise<TeamView> {
  const res = await apiClient.put<{ data: TeamView }>(`/api/teams/${encodeURIComponent(id)}`, body);
  return res.data.data;
}

/** Replace the team's membership with exactly this set of employees. */
export async function setTeamMembers(id: string, employeeIds: string[]): Promise<TeamView> {
  const res = await apiClient.put<{ data: TeamView }>(`/api/teams/${encodeURIComponent(id)}/members`, {
    employeeIds,
  });
  return res.data.data;
}

/** Soft-delete. The leadership team is structural and refuses to archive. */
export async function archiveTeam(id: string): Promise<void> {
  await apiClient.delete(`/api/teams/${encodeURIComponent(id)}`);
}
